import { getRemotionEnvironment } from 'remotion'

/**
 * Agentation, the annotate-and-copy toolbar the web app mounts in dev
 * (`apps/tanstack/src/routes/__root.tsx`), in Remotion Studio. The Studio
 * draws the selected composition in its own document, so the toolbar can pick
 * a template's elements and copy feedback that names them.
 *
 * Studio only: the Lambda site is bundled from this same entry, so the guard
 * is both the build mode (webpack drops the branch in a production bundle)
 * and the runtime environment (never while rendering). It mounts in its own
 * React root because the Studio, not this app, owns the tree `registerRoot`
 * renders into; the id makes a hot reload of the entry a no-op.
 *
 * The Studio's single-key shortcuts (`g`, `o`, space…) listen on `window` and
 * stand down only when `document.activeElement` is an input or a textarea.
 * Agentation's note field lives in a shadow root, so `activeElement` is its
 * host and every keystroke typed into a note fired a shortcut (and the
 * Studio's `preventDefault` swallowed the character). The toolbar portals to
 * `document.body`, outside this root, so key events from a text field inside
 * it stop at `document`, which bubbles before `window`; the field's own
 * handlers have run by then, and the Studio's own inputs are untouched.
 */
const TOOLBAR = 'agentation-toolbar'

export function mountAgentation() {
  if (process.env.NODE_ENV === 'production') return
  if (!getRemotionEnvironment().isStudio) return
  if (document.getElementById('agentation-root')) return

  const host = document.createElement('div')
  host.id = 'agentation-root'
  document.body.appendChild(host)
  for (const type of ['keydown', 'keyup', 'keypress'] as const) {
    document.addEventListener(type, (event) => {
      const path = event.composedPath()
      const inToolbar = path.some(
        (node) => node instanceof Element && node.localName === TOOLBAR,
      )
      if (inToolbar && isTextField(path[0])) event.stopPropagation()
    })
  }

  void Promise.all([import('react-dom/client'), import('agentation')]).then(
    ([{ createRoot }, { Agentation }]) => {
      createRoot(host).render(<Agentation />)
    },
  )
}

function isTextField(target: EventTarget | undefined) {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  )
}
